import "../../styles/movie-table.scss";
import moviesData from "../../data/movies.json";
import imdbIcon from "../../icons/IMDB_Logo_2016.svg";
import rottenTomatoesIcon from "../../icons/Rotten_Tomatoes.png";
import letterboxdIcon from "../../icons/letterboxd-decal-dots-pos-rgb.svg";
import React, { useState, useMemo } from "react";

const MovieNight = () => {
  const [movies] = useState(moviesData);
  const [sortConfig, setSortConfig] = useState({
    key: "watchDate",
    direction: "descending",
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    title: true,
    year: true,
    pickedBy: true,
    watchDate: true,
    runtime: true,
    director: true,
    genres: true,
    links: true,
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    pickedBy: "",
    genre: "",
    director: "",
    decade: "",
  });

  const service_icons = {
    imdb: imdbIcon,
    rottenTomatoes: rottenTomatoesIcon,
    letterboxd: letterboxdIcon,
  };

  // Extract unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const pickedBySet = new Set();
    const genreSet = new Set();
    const directorSet = new Set();
    const decadeSet = new Set();

    movies.forEach((movie) => {
      if (movie.pickedBy) {
        // Consolidate all "Guest ..." variants into a single "Guest" entry
        if (movie.pickedBy.startsWith("Guest")) {
          pickedBySet.add("Guest");
        } else {
          pickedBySet.add(movie.pickedBy);
        }
      }
      if (movie.director) directorSet.add(movie.director);
      if (movie.genres) movie.genres.forEach((g) => genreSet.add(g));
      if (movie.year) {
        const decade = Math.floor(parseInt(movie.year) / 10) * 10;
        if (!isNaN(decade)) decadeSet.add(decade);
      }
    });

    return {
      pickedBy: [...pickedBySet].sort(),
      genres: [...genreSet].sort(),
      directors: [...directorSet].sort(),
      decades: [...decadeSet].sort((a, b) => b - a),
    };
  }, [movies]);

  // Filter movies
  const filteredMovies = useMemo(() => {
    return movies.filter((movie) => {
      // Search filter (searches title, director, genres)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const titleMatch = movie.title?.toLowerCase().includes(searchLower);
        const directorMatch = movie.director
          ?.toLowerCase()
          .includes(searchLower);
        const genreMatch = movie.genres?.some((g) =>
          g.toLowerCase().includes(searchLower)
        );
        if (!titleMatch && !directorMatch && !genreMatch) return false;
      }

      // Picked By filter
      if (filters.pickedBy) {
        if (filters.pickedBy === "Guest") {
          if (!movie.pickedBy?.startsWith("Guest")) return false;
        } else if (movie.pickedBy !== filters.pickedBy) {
          return false;
        }
      }

      // Genre filter
      if (filters.genre && !movie.genres?.includes(filters.genre)) {
        return false;
      }

      // Director filter
      if (filters.director && movie.director !== filters.director) {
        return false;
      }

      // Decade filter
      if (filters.decade) {
        const movieDecade = Math.floor(parseInt(movie.year) / 10) * 10;
        if (movieDecade !== parseInt(filters.decade)) return false;
      }

      return true;
    });
  }, [movies, filters]);

  // Sort filtered movies
  const sortedMovies = useMemo(() => {
    let sortableMovies = [...filteredMovies];
    sortableMovies.sort((a, b) => {
      const va = a[sortConfig.key] ? a[sortConfig.key] : "";
      const vb = b[sortConfig.key] ? b[sortConfig.key] : "";
      if (va < vb) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (va > vb) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
    return sortableMovies;
  }, [filteredMovies, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      pickedBy: "",
      genre: "",
      director: "",
      decade: "",
    });
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  const SortableHeader = ({ label, sortKey }) =>
    visibleColumns[sortKey] ? (
      <th className={sortKey}>
        <button onClick={() => requestSort(sortKey)}>
          {label}
          <span aria-hidden="true">
            {sortConfig.key === sortKey
              ? sortConfig.direction === "ascending"
                ? " ▲"
                : " ▼"
              : ""}
          </span>
        </button>
      </th>
    ) : null;

  const LinkCell = ({ service, sid, icon, title }) => {
    let link;
    if (service === "imdb") {
      link = `https://www.imdb.com/title/${sid}/`;
    } else if (service === "rottenTomatoes") {
      if (sid.startsWith("tv/")) {
        link = `https://www.rottentomatoes.com/${sid}`;
      } else {
        link = `https://www.rottentomatoes.com/m/${sid}`;
      }
    } else if (service === "letterboxd") {
      link = `https://letterboxd.com/film/${sid}/`;
    } else {
      return null;
    }
    return !sid ? null : (
      <a href={link}>
        <img
          src={icon}
          alt={`${service} link for ${title}`}
          style={{ width: "38px", height: "32px" }}
        />
      </a>
    );
  };

  const watchYearCounts = useMemo(() => {
    return movies.reduce((acc, movie) => {
      const year = movie.watchDate
        ? new Date(movie.watchDate).getFullYear()
        : "Unknown";
      acc[year] = (acc[year] || 0) + 1;
      return acc;
    }, {});
  }, [movies]);

  const sortParsableYears = (yearA, yearB) => {
    const a = parseInt(yearA);
    const b = parseInt(yearB);
    if (isNaN(a)) return 1;  // Unknown last
    if (isNaN(b)) return -1;
    return b - a; // descending
  };

  return (
    <div id="root">
      <h1 style={{ textAlign: "center" }}>Movie Night</h1>
      <div className="movie-stats">
        <span className="total-count">
          <strong>{movies.length}</strong> movies watched
        </span>
        <span className="year-counts">
          {Object.entries(watchYearCounts)
            .sort(([yearA], [yearB]) => sortParsableYears(yearA, yearB))
            .map(([year, count]) => (
              <span key={year} className="year-count">
                {year}: {count}
              </span>
            ))}
        </span>
      </div>

      {/* Column Toggles */}
      <div className="table-controls">
        <div className="column-toggles">
          <span className="control-label">Show columns:</span>
          {[
            { key: "title", label: "Title" },
            { key: "year", label: "Year" },
            { key: "pickedBy", label: "Picked By" },
            { key: "watchDate", label: "Watch Date" },
            { key: "runtime", label: "Runtime" },
            { key: "director", label: "Director" },
            { key: "genres", label: "Genres" },
            { key: "links", label: "Links" },
          ].map(({ key, label }) => (
            <label key={key} className="column-toggle">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={() => toggleColumn(key)}
              />
              {label}
            </label>
          ))}
        </div>

        {/* Filters */}
        <div className="filters">
          <input
            type="text"
            placeholder="Search..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="search-input"
          />

          <select
            value={filters.pickedBy}
            onChange={(e) => updateFilter("pickedBy", e.target.value)}
          >
            <option value="">All Pickers</option>
            {filterOptions.pickedBy.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            value={filters.genre}
            onChange={(e) => updateFilter("genre", e.target.value)}
          >
            <option value="">All Genres</option>
            {filterOptions.genres.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>

          <select
            value={filters.director}
            onChange={(e) => updateFilter("director", e.target.value)}
          >
            <option value="">All Directors</option>
            {filterOptions.directors.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          <select
            value={filters.decade}
            onChange={(e) => updateFilter("decade", e.target.value)}
          >
            <option value="">All Decades</option>
            {filterOptions.decades.map((d) => (
              <option key={d} value={d}>
                {d}s
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters">
              Clear Filters
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="filter-status">
            Showing {sortedMovies.length} of {movies.length} movies
          </div>
        )}
      </div>

      <div className="movie-table">
        <table
          className="sortable"
          style={{ marginLeft: "auto", marginRight: "auto" }}
        >
          <caption>
            <h3>Movie Picks</h3>
          </caption>
          <thead>
            <tr>
              <SortableHeader label="Movie Title" sortKey="title" />
              <SortableHeader label="Release Year" sortKey="year" />
              <SortableHeader label="Runtime" sortKey="runtime" />
              <SortableHeader label="Director" sortKey="director" />
              {visibleColumns.genres && <th className="genres">Genres</th>}
              <SortableHeader label="Picked By" sortKey="pickedBy" />
              <SortableHeader label="Watch Date" sortKey="watchDate" />
              {visibleColumns.links && <th className="links">Links</th>}
            </tr>
          </thead>
          <tbody>
            {sortedMovies.map((movie, index) => (
              <tr key={index}>
                {visibleColumns.title && (
                  <td className="movie-title">
                    {movie.title}
                    {movie.version && (
                      <span className="version-tag"> ({movie.version})</span>
                    )}
                  </td>
                )}
                {visibleColumns.year && (
                  <td className="movie-year">{movie.year}</td>
                )}
                {visibleColumns.runtime && (
                  <td className="runtime">
                    {movie.runtime ? `${movie.runtime}m` : ""}
                  </td>
                )}
                {visibleColumns.director && (
                  <td className="director">{movie.director || ""}</td>
                )}
                {visibleColumns.genres && (
                  <td className="genres">
                    {movie.genres?.map((genre, i) => (
                      <span key={i} className="genre-tag">
                        {genre}
                      </span>
                    ))}
                  </td>
                )}
                {visibleColumns.pickedBy && (
                  <td className="picked-by">{movie.pickedBy}</td>
                )}
                {visibleColumns.watchDate && (
                  <td className="watch-date">{movie.watchDate}</td>
                )}
                {visibleColumns.links && (
                  <td className="links">
                    {["imdb", "rottenTomatoes", "letterboxd"].map(
                      (service, i) => (
                        <LinkCell
                          key={i}
                          service={service}
                          sid={movie[`${service}Id`]}
                          icon={service_icons[service]}
                          title={movie.title}
                        />
                      )
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MovieNight;